import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Save, Upload } from 'lucide-react';

const AdminInvoiceSettings = () => {
  const [settings, setSettings] = useState({
    firm_name: '', shop_name: '', website: '', gst_rate: 5, gst_number: '',
    tagline: '', footer_text: '', logo_url: '', address: '', phone: '', email: '',
    declaration_text: '', jurisdiction_text: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const saveSettings = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/invoice/settings`, settings, { withCredentials: true });
      toast.success('Invoice settings saved');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(prev => ({ ...prev, logo_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="container mx-auto px-4 py-10 pt-16 max-w-2xl">
        <h1 className="text-3xl font-black font-heading mb-8" data-testid="invoice-settings-heading">Invoice Settings</h1>

        <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
          <p className="text-sm text-muted-foreground mb-4">These settings appear on all generated invoice PDFs with QR code and barcode.</p>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Firm Name</Label><Input value={settings.firm_name} onChange={(e) => setSettings({...settings, firm_name: e.target.value})} data-testid="invoice-firm-name" /></div>
            <div><Label>Shop Name</Label><Input value={settings.shop_name} onChange={(e) => setSettings({...settings, shop_name: e.target.value})} data-testid="invoice-shop-name" /></div>
          </div>
          <div><Label>Tagline</Label><Input value={settings.tagline} onChange={(e) => setSettings({...settings, tagline: e.target.value})} data-testid="invoice-tagline" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Website</Label><Input value={settings.website} onChange={(e) => setSettings({...settings, website: e.target.value})} data-testid="invoice-website" /></div>
            <div><Label>GST Number</Label><Input value={settings.gst_number} onChange={(e) => setSettings({...settings, gst_number: e.target.value})} data-testid="invoice-gst-number" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>GST Rate (%)</Label><Input type="number" step="0.1" value={settings.gst_rate} onChange={(e) => setSettings({...settings, gst_rate: parseFloat(e.target.value) || 0})} data-testid="invoice-gst-rate" /></div>
            <div>
              <Label>Logo</Label>
              <div className="flex gap-2">
                <Input value={settings.logo_url} onChange={(e) => setSettings({...settings, logo_url: e.target.value})} placeholder="Paste URL or upload" data-testid="invoice-logo-url" className="flex-1" />
                <Button type="button" variant="outline" size="sm" className="flex-shrink-0 relative" disabled={uploading} data-testid="invoice-logo-upload-button">
                  <Upload className="h-4 w-4 mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="invoice-logo-file-input" />
                </Button>
              </div>
              {settings.logo_url && <img src={settings.logo_url} alt="Logo preview" className="mt-2 h-12 object-contain" data-testid="invoice-logo-preview" />}
            </div>
          </div>
          <div><Label>Address</Label><Textarea value={settings.address} onChange={(e) => setSettings({...settings, address: e.target.value})} data-testid="invoice-address" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Phone</Label><Input value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} data-testid="invoice-phone" /></div>
            <div><Label>Email</Label><Input value={settings.email} onChange={(e) => setSettings({...settings, email: e.target.value})} data-testid="invoice-email" /></div>
          </div>
          <div><Label>Footer Text</Label><Textarea value={settings.footer_text} onChange={(e) => setSettings({...settings, footer_text: e.target.value})} data-testid="invoice-footer-text" /></div>
          <div>
            <Label>Declaration Text</Label>
            <Textarea
              value={settings.declaration_text || ''}
              onChange={(e) => setSettings({ ...settings, declaration_text: e.target.value })}
              placeholder="We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct."
              rows={3}
              data-testid="invoice-declaration-text"
            />
            <p className="text-xs text-muted-foreground mt-1">Appears at the bottom-left of the invoice PDF, above the jurisdiction line.</p>
          </div>
          <div>
            <Label>Jurisdiction / Subject to</Label>
            <Input
              value={settings.jurisdiction_text || ''}
              onChange={(e) => setSettings({ ...settings, jurisdiction_text: e.target.value })}
              placeholder="Subject to Satara Jurisdiction"
              data-testid="invoice-jurisdiction-text"
            />
            <p className="text-xs text-muted-foreground mt-1">Printed below the declaration on every invoice PDF.</p>
          </div>

          <Button onClick={saveSettings} className="w-full" data-testid="save-invoice-settings-button">
            <Save className="mr-2 h-4 w-4" /> Save Invoice Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminInvoiceSettings;
